import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Video, VideoOff, Mic, MicOff, Maximize2, Minimize2, PhoneOff } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { getInitials } from '@/lib/utils';
import webRTCService from '@/lib/webrtc';
import wsService from '@/lib/websocket';

export const CallModal = ({ isOpen, onClose, callType, isIncoming, callerInfo, targetUser, chatId }) => {
  const callInitiatedRef = useRef(false);
  const [isMuted, setIsMuted] = useState(false);
  const [isVideoOff, setIsVideoOff] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [callDuration, setCallDuration] = useState(0);
  
  const localVideoRef = useRef(null);
  const remoteVideoRef = useRef(null);
  const remoteAudioRef = useRef(null); 
  const durationIntervalRef = useRef(null);
  const otherUser = isIncoming ? callerInfo : targetUser;

  // --- PUNKT 2: Aggressive Audio-Konfiguration (Hier integriert) ---
  const playRemoteStream = (stream) => {
    // 1. Video
    if (remoteVideoRef.current && remoteVideoRef.current.srcObject !== stream) {
      remoteVideoRef.current.srcObject = stream;
    }
    
    // 2. Audio mit "Force Play" und Event Listener
    if (remoteAudioRef.current && remoteAudioRef.current.srcObject !== stream) {
        console.log("🔊 Setze Remote Audio Stream...");
        const audioElement = remoteAudioRef.current;
        
        // 🔥 Aggressive Konfiguration
        audioElement.srcObject = stream;
        audioElement.muted = false;        // Nicht muted
        audioElement.autoplay = true;      // Autoplay
        audioElement.playsInline = true;   // Für Mobile
        audioElement.volume = 1.0;         // Maximale Lautstärke
        
        // ⚡ Erzwinge Play nach User-Interaction
        const forcePlay = async () => {
            try {
                await audioElement.play();
                console.log("✅ Remote Audio startet!");
            } catch (e) {
                console.warn("⚠️ Autoplay blockiert:", e.message);
                
                // Fallback: User-Click erforderlich
                // Wir fügen einen Listener hinzu, der beim nächsten Klick (egal wo) das Audio startet
                const playOnClick = () => {
                    audioElement.play().then(() => {
                         console.log("✅ Audio durch Klick gestartet");
                         document.removeEventListener('click', playOnClick);
                    }).catch(err => console.log("Klick-Start fehlgeschlagen", err));
                };
                document.addEventListener('click', playOnClick);
            }
        };
        forcePlay();
    }
  };

  useEffect(() => {
    if (!isOpen) return;
    if (callInitiatedRef.current) return;
    callInitiatedRef.current = true;

    const setupStreams = async () => {
      try {
        if (isIncoming) {          
          startDurationTimer();
          attachStreams(); 
        } else {
          await startOutgoingCall();
          startDurationTimer();
        }
      } catch (error) {
        console.error('Setup Error:', error);
        onClose();
      }
    };
    setupStreams();
    return () => {
      callInitiatedRef.current = false;
      if (durationIntervalRef.current) clearInterval(durationIntervalRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const attachStreams = () => {
    const localStream = webRTCService.getLocalStream();
    if (localVideoRef.current && localStream) {
      localVideoRef.current.srcObject = localStream;
      localVideoRef.current.muted = true; 
    }
    const remoteStream = webRTCService.getRemoteStream();
    if (remoteStream) playRemoteStream(remoteStream);

    webRTCService.onRemoteStream = (stream) => playRemoteStream(stream);
  };

  const startOutgoingCall = async () => {
    const localStream = await webRTCService.initializeCall(callType);
    if (localVideoRef.current) {
        localVideoRef.current.srcObject = localStream;
        localVideoRef.current.muted = true; 
    }
    webRTCService.onRemoteStream = (stream) => playRemoteStream(stream);
    webRTCService.onIceCandidate = (candidate) => {
      wsService.send({ type: 'ice_candidate', target_user_id: targetUser.id, candidate });
    };
    const offer = await webRTCService.createOffer();
    wsService.send({ type: 'call_offer', target_user_id: targetUser.id, call_type: callType, offer, chat_id: chatId });
  };

  const toggleMute = () => { 
    setIsMuted(prev => { const next = !prev; webRTCService.toggleAudio(next); return next; });
  };
  const toggleVideo = () => { 
    setIsVideoOff(prev => { const next = !prev; webRTCService.toggleVideo(next); return next; });
  };
  
  const startDurationTimer = () => { if (durationIntervalRef.current) clearInterval(durationIntervalRef.current); durationIntervalRef.current = setInterval(() => setCallDuration(d => d + 1), 1000); };
  const formatDuration = (s) => { const m = Math.floor(s / 60); const sc = s % 60; return `${m.toString().padStart(2, '0')}:${sc.toString().padStart(2, '0')}`; };

  // Globaler Click-Handler als Backup
  const forceAudioStart = () => {
      if (remoteAudioRef.current) {
          remoteAudioRef.current.play().catch(() => {});
      }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div 
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} 
        className={`fixed inset-0 z-[9999] bg-slate-900 flex flex-col ${isFullscreen ? '' : 'p-4'}`}
        onClick={forceAudioStart}
      >
        <audio ref={remoteAudioRef} autoPlay playsInline controls={false} style={{ position: 'absolute', opacity: 0, pointerEvents: 'none' }} />

        <div className="absolute top-4 left-4 right-4 z-10 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Avatar className="h-12 w-12 border-2 border-white/20">
              <AvatarImage src={otherUser?.avatar} />
              <AvatarFallback className="bg-slate-700 text-white">{getInitials(otherUser?.username)}</AvatarFallback>
            </Avatar>
            <div>
              <h3 className="text-white font-semibold">{otherUser?.username}</h3>
              <p className="text-white/60 text-sm">{formatDuration(callDuration)}</p>
            </div>
          </div>
          <Button variant="ghost" size="icon" onClick={() => setIsFullscreen(!isFullscreen)} className="text-white hover:bg-white/10">
            {isFullscreen ? <Minimize2 className="w-5 h-5" /> : <Maximize2 className="w-5 h-5" />}
          </Button>
        </div>

            <div className="flex-1 relative bg-black rounded-xl overflow-hidden flex items-center justify-center">
            <video ref={remoteVideoRef} autoPlay playsInline className={callType === 'video' ? "w-full h-full object-cover" : "hidden"} muted />
            {callType === 'video' && (
                <motion.div drag dragConstraints={{ left: 0, right: 0, top: 0, bottom: 0 }} className="absolute bottom-24 right-4 w-32 h-44 rounded-2xl overflow-hidden bg-slate-800 shadow-xl border border-white/10">
                <video ref={localVideoRef} autoPlay playsInline muted className={`w-full h-full object-cover ${isVideoOff ? 'hidden' : ''}`} />
                {isVideoOff && <div className="w-full h-full flex items-center justify-center bg-slate-800"><VideoOff className="w-8 h-8 text-white/50" /></div>}
                </motion.div>
            )}
            {callType !== 'video' && (
                <motion.div animate={{ scale: [1, 1.05, 1] }} transition={{ repeat: Infinity, duration: 2 }} className="text-center relative z-10">
                <Avatar className="h-40 w-40 mx-auto border-4 border-emerald-500/30 ring-4 ring-emerald-500/10">
                    <AvatarImage src={otherUser?.avatar} />
                    <AvatarFallback className="bg-slate-700 text-white text-5xl">{getInitials(otherUser?.username)}</AvatarFallback>
                </Avatar>
                <p className="text-white/50 mt-4 text-sm">Tippen, falls kein Ton...</p>
                </motion.div>
            )}
        </div>

        <div className="absolute bottom-8 left-0 right-0 flex items-center justify-center gap-6 z-20">
          <Button size="lg" onClick={toggleMute} className={`h-16 w-16 rounded-full transition-all ${isMuted ? 'bg-white text-slate-900' : 'bg-slate-800/80 backdrop-blur text-white'}`}>
            {isMuted ? <MicOff className="w-6 h-6" /> : <Mic className="w-6 h-6" />}
          </Button>
          {callType === 'video' && (
            <Button size="lg" onClick={toggleVideo} className={`h-16 w-16 rounded-full transition-all ${isVideoOff ? 'bg-white text-slate-900' : 'bg-slate-800/80 backdrop-blur text-white'}`}>
              {isVideoOff ? <VideoOff className="w-6 h-6" /> : <Video className="w-6 h-6" />}
            </Button>
          )}
          <Button size="lg" onClick={onClose} className="h-16 w-16 rounded-full bg-red-500 hover:bg-red-600 shadow-lg shadow-red-500/20">
            <PhoneOff className="w-7 h-7 text-white" />
          </Button>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default CallModal;